<template>
  <div v-show="show" :transition="transition">
    <div class="modal" @click.self="clickMask">
      <div class="modal-dialog" :class="modalClass">
        <div class="modal-content">
          <div class="modal-header">
            <!-- The modal header slot. By default includes a title and the close button -->
            <slot name="header">
              <h1 v-if="title !== undefined" class="modal-title h4">
                <!-- The modal header title slot—uses H1 -->
                <slot name="title">
                  {{ title }}
                </slot>
              </h1>
              <button class="close" :aria-label="'Close Modal ' + title" @click="cancel">
                x
              </button>
            </slot>
          </div>
          <div class="modal-body">
            <!-- The modal body slot -->
            <slot />
          </div>
          <div class="modal-footer">
            <!-- The modal footer slot. By default, includes a slot for footer content, plus OK and Cancel buttons -->
            <slot name="footer">
              <!-- Slot for any footer content before buttons -->
              <slot name="footer-content" />
              <button type="button" :class="cancelClass" @click="cancel">
                {{ cancelText }}
              </button>
              <button type="button" :class="okClass" :disabled="okDisable" @click="ok">
                {{ okText }}
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop in" />
  </div>
</template>
<script>
/**
 * Bootstrap Style Modal Component for Vue
 * Depend on Bootstrap.css
 */

export default {
  // emits: ['ok', 'cancel', 'update:show'],
  props: {
    /**
     * Controls the visibility of the modal
     */
    show: {
      type: Boolean,
      twoWay: true,
      default: false
    },
    /**
     * The modal title
     */
    title: {
      type: String,
    },
    /**
     * Use small modal size
     * @deprecated All the sizes should be combined into one prop
     */
    small: {
      type: Boolean,
      default: false
    },
    /**
     * Use large modal size
     * @deprecated All the sizes should be combined into one prop
     */
    large: {
      type: Boolean,
      default: false
    },
    /**
     * Use full modal size
     * @deprecated All the sizes should be combined into one prop
     */
    full: {
      type: Boolean,
      default: false
    },
    /**
     * When true, the modal cannot be closed by clicking the mask layer
     */
    force: {
      type: Boolean,
      default: false
    },
    /**
     * Custom component transition
     */
    transition: {
      type: String,
      default: 'modal'
    },
    /**
     * Confirm button text
     */
    okText: {
      type: String,
      default: 'OK'
    },
    /**
     * Cancel button text
     */
    cancelText: {
      type: String,
      default: 'Cancel'
    },
    /**
     * Confirm button className
     */
    okClass: {
      type: String,
      default: 'btn btn-primary'
    },
    /**
     * Cancel button className
     */
    cancelClass: {
      type: String,
      default: 'btn btn-link'
    },
    /**
     * Disable the OK button
     */
    okDisable: {
      type: Boolean,
      default: false
    },
    /**
     * Close Modal when you click OK. The default is false, the parent component controls prop.show to close
     */
    closeWhenOK: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      duration: null
    };
  },
  computed: {
    /**
     * Computes modal size classes
     */
    modalClass () {
      return {
        'modal-lg': this.large,
        'modal-sm': this.small,
        'modal-full': this.full
      }
    }
  },
  watch: {
    /**
     * Watches the show prop to toggle body scroll bar
     */
    show (value) {
      // Remove the body scroll bar when displaying to prevent double scroll bars
      // .modal-open applies `overflow: hidden;`
      if (value) {
        document.body.className += ' modal-open';
      } else {
        // Add the body scroll bar after the modal animation ends
        if (!this.duration) {
          this.duration = window.getComputedStyle(this.$el)['transition-duration'].replace('s', '') * 1000;
        }

        window.setTimeout(() => {
          document.body.className = document.body.className.replace(/\s?modal-open/, '');
        }, this.duration || 0);
      }
    }
  },
  created () {
    if (this.show) {
      document.body.className += ' modal-open';
    }
  },
  beforeDestroy () {
    document.body.className = document.body.className.replace(/\s?modal-open/, '');
  },
  methods: {
    /**
     * Emits ok event and closes modal
     */
    ok () {
      this.$emit('ok');
      if(this.closeWhenOK) {
        this.$emit('update:show', false);
      }
    },
    /**
     * Emits cancel event and closes modal
     */
    cancel () {
      this.$emit('cancel');
      this.$emit('update:show', false);
    },
    /**
     * Handles mask click to close modal if not forced
     */
    clickMask () {
      if (!this.force) {
        this.cancel();
      }
    }
  }
};
</script>
<style scoped>
.modal {
  display: block;
}
.modal-transition {
  transition: all .6s ease;
}
.modal-leave {
  /* The style is useless, but you can make the transitionEnd of the root tag take effect to remove the modal-leave */
  border-radius: 1px !important;
}
.modal-transition .modal-dialog, .modal-transition .modal-backdrop {
  transition: all .5s ease;
}
.modal-enter .modal-dialog, .modal-leave .modal-dialog {
  opacity: 0;
  transform: translateY(-30%);
}
.modal-enter .modal-backdrop, .modal-leave .modal-backdrop {
  opacity: 0;
}
</style>
